import { Controller } from "@hotwired/stimulus"
import { trackSuggestionEvent } from "../utils/assistant_suggestion_tracking"

/**
 * A Stimulus controller for the assistant message input.
 *
 * Two responsibilities:
 *  - Enter-to-submit on the textarea (Shift+Enter inserts a newline; submit is
 *    skipped when the submit button is already disabled).
 *  - Character counter that surfaces only as the user approaches the limit,
 *    and disables the submit button when the limit is exceeded.
 *
 * Counter visual states (driven by ratio = current / max):
 *  - `< 90%`: hidden
 *  - `>= 90%` and `< 95%`: neutral
 *  - `>= 95%` and `< 100%`: warning
 *  - `>= 100%`: error + submit button disabled
 *
 * Values:
 *  - `maxLength` (Number): character cap. When omitted, the counter is inert.
 *  - `labelTemplate` (String): visible label format with `%{current}` and `%{max}` placeholders.
 *  - `ariaTemplate` (String): aria-label format with `%{current}` and `%{max}` placeholders.
 *  - `draftKey` (String, optional): conversation-specific key used to preserve unsent text.
 *
 * Targets:
 *  - `input` (textarea): the input being measured.
 *  - `counter` (span): the element that displays the counter.
 *  - `submit` (button, optional): the form submit button to disable when over the limit.
 *    If missing, falls back to `#send_button` or the first `button[type="submit"]` in scope.
 *
 * @example
 * <div data-controller="assistant-ui"
 *      data-assistant-ui-max-length-value="5000"
 *      data-assistant-ui-label-template-value="%{current} / %{max}"
 *      data-assistant-ui-aria-template-value="%{current} of %{max} characters">
 *   <textarea data-assistant-ui-target="input"
 *             data-action="input->assistant-ui#updateCounter keydown->assistant-ui#handleKeydown"></textarea>
 *   <span data-assistant-ui-target="counter" class="hidden" aria-live="polite"></span>
 *   <button type="submit" data-assistant-ui-target="submit">Send</button>
 * </div>
 */
export default class extends Controller {
  static targets = ["input", "counter", "submit"]
  static values = {
    maxLength: Number,
    labelTemplate: { type: String, default: "%{current} / %{max}" },
    ariaTemplate: { type: String, default: "%{current} of %{max} characters" },
    draftKey: String
  }

  connect () {
    this.form = this.element.closest('form')
    this._boundOnSubmitStart = this.onSubmitStart.bind(this)
    this._boundOnSubmitEnd = this.onSubmitEnd.bind(this)
    this._boundOnPageMorph = this._restoreDraft.bind(this)
    this.form?.addEventListener('turbo:submit-start', this._boundOnSubmitStart)
    this.form?.addEventListener('turbo:submit-end', this._boundOnSubmitEnd)
    // Pages are re-rendered by morphing (see `turbo_refreshes_with` in
    // shared/_head), which keeps this element connected — so connect() does not
    // run again — while syncing the input with the server's empty one. Restore
    // the draft again after each morph or the unsent text is lost on screen.
    document.addEventListener('turbo:morph', this._boundOnPageMorph)

    this._restoreDraft()
    this.updateCounter()
  }

  disconnect () {
    this.form?.removeEventListener('turbo:submit-start', this._boundOnSubmitStart)
    this.form?.removeEventListener('turbo:submit-end', this._boundOnSubmitEnd)
    document.removeEventListener('turbo:morph', this._boundOnPageMorph)
  }

  handleKeydown (event) {
    if (event.key !== 'Enter') return
    if (event.shiftKey) return

    event.preventDefault()
    event.stopPropagation()

    const form = event.target.form
    if (!form) return

    const sendButton = form.querySelector('#send_button')
    if (sendButton?.disabled) return

    form.requestSubmit()
  }

  updateCounter () {
    this._storeDraft()

    if (!this.hasCounterTarget) return
    if (!this.maxLengthValue) return

    const input = this._inputElement()
    if (!input) return

    const current = input.value.length
    const max = this.maxLengthValue
    const ratio = current / max

    const counter = this.counterTarget

    if (ratio < 0.9) {
      counter.classList.add("hidden")
      counter.textContent = ""
      counter.removeAttribute("data-state")
      this._setSendButtonDisabled(false)
      return
    }

    counter.classList.remove("hidden")
    counter.textContent = this._format(this.labelTemplateValue, current, max)
    counter.setAttribute(
      "aria-label",
      this._format(this.ariaTemplateValue, current, max)
    )

    if (ratio >= 1) {
      counter.dataset.state = "error"
      counter.classList.remove("text-gray-500", "text-amber-600")
      counter.classList.add("text-red-600", "font-medium")
      this._setSendButtonDisabled(true)
    } else if (ratio >= 0.95) {
      counter.dataset.state = "warning"
      counter.classList.remove("text-gray-500", "text-red-600", "font-medium")
      counter.classList.add("text-amber-600")
      this._setSendButtonDisabled(false)
    } else {
      counter.dataset.state = "neutral"
      counter.classList.remove("text-amber-600", "text-red-600", "font-medium")
      counter.classList.add("text-gray-500")
      this._setSendButtonDisabled(false)
    }
  }

  onSubmitStart () {
    const input = this._inputElement()
    if (input) input.readOnly = true
  }

  onSubmitEnd (event) {
    if (event.detail.success) {
      trackSuggestionEvent('sent', this._suggestionSourceInput()?.value)
      this._clearSuggestionSource()
    }

    const input = this._inputElement()
    if (!input) return

    input.readOnly = false
    if (event.detail.success) input.value = ''
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  // The suggestion chip that filled the composer is attributed to the message it
  // produced and to no other: the sent message carries the value, and the next
  // one starts without it. Cleared only on success so a rejected submission can
  // be retried with its attribution intact.
  _clearSuggestionSource () {
    const source = this._suggestionSourceInput()
    if (source) source.value = ''
  }

  _suggestionSourceInput () {
    return this.element.querySelector('input[name="suggestion_source"]')
  }

  _inputElement () {
    if (this.hasInputTarget) return this.inputTarget
    return this.element.querySelector('textarea[name="message[question]"]')
  }

  _sendButton () {
    if (this.hasSubmitTarget) return this.submitTarget
    return this.element.querySelector('#send_button, button[type="submit"]')
  }

  _setSendButtonDisabled (disabled) {
    const button = this._sendButton()
    if (!button) return
    if (button.dataset.sendButtonProcessingValue === "true") return

    if (disabled) {
      button.disabled = true
      button.dataset.assistantUiOverLimit = "true"
    } else if (button.dataset.assistantUiOverLimit === "true") {
      button.disabled = false
      delete button.dataset.assistantUiOverLimit
    }
  }

  _storeDraft () {
    const storageKey = this._draftStorageKey()
    const input = this._inputElement()
    if (!storageKey || !input) return

    if (!this._draftEnabled()) {
      localStorage.removeItem(storageKey)
      return
    }

    if (input.value) {
      localStorage.setItem(storageKey, input.value)
    } else {
      localStorage.removeItem(storageKey)
    }
  }

  _restoreDraft () {
    const storageKey = this._draftStorageKey()
    const input = this._inputElement()
    if (!storageKey || !input || input.value || !this._draftEnabled()) return

    const draft = localStorage.getItem(storageKey)
    if (!draft) return

    input.value = draft
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  _draftEnabled () {
    const omniSearchMode = this.element.dataset.omniSearchCurrentModeValue

    return !omniSearchMode || omniSearchMode === 'assistant'
  }

  _draftStorageKey () {
    if (!this.hasDraftKeyValue) return null

    return `assistant-message-draft:${this.draftKeyValue}`
  }

  _format (template, current, max) {
    return template
      .replace("%{current}", current)
      .replace("%{max}", max)
  }
}
